<template>
  <div class="payment-method-selection">
    <!-- Thank You Message -->
    <div class="success-container">
      <div class="success-card">
        <div class="success-icon-wrapper">
          <div class="success-icon-circle">
            <font-awesome-icon :icon="['fas', 'check']" class="check-icon" />
          </div>
        </div>

        <h2 class="success-title">
          {{ $translate("Thank You for Your Order!") }}
        </h2>
        <div
          class="success-subtitle"
          v-html="$translate('thank_you_subtitle')"
        ></div>
        <p class="success-text">
          {{
            $translate(
              "Please proceed with payment using one of the methods below."
            )
          }}
        </p>

        <button @click="showProformaModal = true" class="proforma-button">
          <font-awesome-icon :icon="['fas', 'envelope']" class="button-icon" />
          <span>{{ $translate("Send Proforma Invoice to E-mail") }}</span>
        </button>
      </div>
    </div>

    <!-- Bank Transfer Details -->
    <div class="payment-card bank-transfer-card">
      <div class="payment-card-header">
        <h3 class="payment-card-title">
          <font-awesome-icon
            :icon="['fas', 'university']"
            class="payment-card-icon"
          />
          {{ $translate("Bank Transfer Details") }}
        </h3>
      </div>

      <div class="payment-card-content">
        <div class="bank-transfer-content">
          <div v-if="qrCode" class="qr-code-container">
            <div class="qr-code-wrapper">
              <img :src="qrCode" alt="QR Code" class="qr-code-image" />
            </div>
            <p class="qr-code-caption">{{ $translate("Scan to Pay") }}</p>
          </div>

          <div class="bank-details-container">
            <div class="bank-details-grid">
              <div class="bank-detail-item">
                <div class="bank-detail-label">
                  {{ $translate("Account Number") }}
                </div>
                <div class="bank-detail-value">
                  {{ bankDetails.bank_account }} / {{ bankDetails.bank_code }}
                </div>
              </div>

              <div class="bank-detail-item">
                <div class="bank-detail-label">{{ $translate("IBAN") }}</div>
                <div class="bank-detail-value secondary">
                  {{ bankDetails.iban }}
                </div>
              </div>

              <div class="bank-detail-item">
                <div class="bank-detail-label">
                  {{ $translate("Variable Symbol") }}
                </div>
                <div class="bank-detail-value highlight">
                  {{ variableSymbol }}
                </div>
              </div>

              <div class="bank-detail-item">
                <div class="bank-detail-label">
                  {{ $translate("Amount to Pay") }}
                </div>
                <div class="bank-detail-value highlight">{{ total }}</div>
              </div>
            </div>

            <a
              :href="proformaUrl"
              class="invoice-download-link"
              target="_blank"
            >
              <font-awesome-icon
                :icon="['fas', 'file-download']"
                class="download-icon"
              />
              <span>{{ $translate("Download Proforma Invoice") }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Alternative Payment Methods -->
    <div
      v-if="paymentMethods.length > 0"
      class="payment-card payment-methods-card"
    >
      <div class="payment-card-header">
        <h3 class="payment-card-title">
          <font-awesome-icon
            :icon="['fas', 'credit-card']"
            class="payment-card-icon"
          />
          {{ $translate("Alternative Payment Methods") }}
        </h3>
      </div>

      <div class="payment-card-content">
        <div class="payment-methods-grid">
          <div
            v-for="method in paymentMethods"
            :key="method.id"
            class="payment-method-item"
            @click="selectPaymentMethod(method)"
          >
            <div class="payment-method-content">
              <img
                :src="method.logo"
                :alt="method.name"
                class="payment-method-logo"
              />
              <div class="payment-method-details">
                <h4 class="payment-method-name">{{ method.name }}</h4>
                <p class="payment-method-description">{{ method.excerpt }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Security Question -->
    <div v-if="securityQuestion" class="payment-card security-card">
      <div class="payment-card-header">
        <h3 class="payment-card-title">
          <font-awesome-icon
            :icon="['fas', 'shield-alt']"
            class="payment-card-icon"
          />
          {{ $translate("Organization Security Question") }}
        </h3>
      </div>

      <div class="payment-card-content">
        <div class="security-question-content">
          <p class="security-question-text">{{ securityQuestion }}</p>

          <div class="security-answer-input">
            <input
              type="text"
              v-model="securityAnswer"
              class="security-input"
              :placeholder="$translate('Your Answer')"
            />
          </div>

          <button
            type="button"
            @click="submitSecurityAnswer"
            class="security-submit-button"
          >
            {{ $translate("Save Answer") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Crypto Payment Methods -->
    <div v-if="paymentData" class="payment-card crypto-card">
      <div class="payment-card-header">
        <h3 class="payment-card-title">
          <font-awesome-icon
            :icon="['fab', 'bitcoin']"
            class="payment-card-icon crypto-icon"
          />
          {{ $translate("Crypto Payment Methods") }}
        </h3>
      </div>

      <div class="payment-card-content">
        <div class="crypto-countdown">
          <div class="countdown-progress">
            <div class="countdown-bar-container">
              <div
                class="countdown-bar"
                :style="{ width: (remainingTime / totalDuration) * 100 + '%' }"
              ></div>
            </div>
            <div class="countdown-time">{{ formatTime(remainingTime) }}</div>
          </div>
        </div>

        <div v-if="remainingTime > 0" class="crypto-methods-container">
          <div class="crypto-methods-grid">
            <div
              v-for="(method, key) in paymentData.methods"
              :key="key"
              class="crypto-method-item"
            >
              <div class="crypto-method-header">
                <h4 class="crypto-method-name">{{ method.name }}</h4>
              </div>

              <div class="crypto-method-content">
                <div class="crypto-amount-info">
                  <div class="crypto-amount">
                    <span class="crypto-amount-label">{{
                      $translate("Amount")
                    }}</span>
                    <span class="crypto-amount-value"
                      >{{ method.amount }} {{ method.rate_currency }}</span
                    >
                  </div>

                  <div class="crypto-rate">
                    <span class="crypto-rate-label"
                      >1 {{ method.rate_currency }}</span
                    >
                    <span class="crypto-rate-value"
                      >{{ method.rate }} {{ paymentData.currency }}</span
                    >
                  </div>
                </div>

                <div class="crypto-address-container">
                  <div class="crypto-address-input-wrapper">
                    <input
                      :value="method.address"
                      class="crypto-address-input"
                      readonly
                    />
                    <button
                      @click="copyToClipboard(method.address)"
                      class="crypto-copy-button"
                    >
                      <font-awesome-icon :icon="['fas', 'copy']" />
                      <span>{{ $translate("Copy") }}</span>
                    </button>
                  </div>
                </div>

                <div v-if="method.qr" class="crypto-qr-container">
                  <img :src="method.qr" alt="QR Code" class="crypto-qr-image" />
                </div>

                <a
                  :href="method.link"
                  class="crypto-wallet-link"
                  target="_blank"
                >
                  <font-awesome-icon
                    :icon="['fas', 'wallet']"
                    class="wallet-icon"
                  />
                  {{ $translate("Open in Wallet") }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="crypto-expired">
          <p class="crypto-expired-message">
            {{ $translate("Payment expired.") }}
          </p>
          <button @click="fetchPaymentDetails" class="crypto-refresh-button">
            <font-awesome-icon :icon="['fas', 'sync']" class="refresh-icon" />
            {{ $translate("Fetch New Payment Details") }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Use Teleport for the modal -->
  <Teleport :to="teleportTarget">
    <div v-if="showProformaModal" class="modern-modal-backdrop">
      <div class="modern-modal">
        <div class="modern-modal-content">
          <div class="modern-modal-header">
            <h3 class="modern-modal-title">
              {{ $translate("proforma_modal_title") }}
            </h3>
            <button @click="showProformaModal = false" class="modern-close-btn">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <div class="modern-modal-body">
            <div class="modern-modal-description">
              {{ $translate("proforma_modal_description") }}
            </div>

            <div class="modern-form-group">
              <label for="invoice-email" class="modern-form-label">
                {{ $translate("Email Address") }}
              </label>
              <div class="modern-input-container">
                <font-awesome-icon
                  :icon="['fas', 'envelope']"
                  class="modern-input-icon"
                />
                <input
                  type="email"
                  id="invoice-email"
                  v-model="proformaEmail"
                  class="modern-form-input"
                  :placeholder="$translate('Enter email address')"
                />
              </div>
              <p v-if="emailError" class="modern-form-error">
                {{ emailError }}
              </p>
            </div>
          </div>

          <div class="modern-modal-footer">
            <button
              @click="showProformaModal = false"
              class="modern-cancel-btn"
            >
              {{ $translate("Cancel") }}
            </button>
            <button
              @click="sendProformaInvoice"
              class="modern-submit-btn"
              :disabled="isSendingInvoice"
            >
              <span v-if="!isSendingInvoice" class="modern-btn-content">
                <font-awesome-icon
                  :icon="['fas', 'paper-plane']"
                  class="modern-btn-icon"
                />
                {{ $translate("Send Invoice") }}
              </span>
              <span v-else class="modern-btn-content">
                <svg class="modern-loading-icon" viewBox="0 0 24 24">
                  <circle
                    class="modern-loading-circle"
                    cx="12"
                    cy="12"
                    r="10"
                  />
                </svg>
                {{ $translate("Sending...") }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, onMounted, watch, onUnmounted, Teleport, computed } from "vue";
import { notify } from "notiwind";
import { translate } from "@/utils/translate";
import { cookie } from "@/utils/cookie"; // Adjust if your session ID utility is named differently
import { trackEvent } from "@/utils/trackingManager";

export default {
  name: "SelectPayment",
  components: {
    Teleport,
  },
  props: {
    attendee: {
      type: Object,
      required: false,
    },
    userEmail: {
      type: String,
      required: false,
      default: "",
    },
  },
  setup(props) {
    // Inject cart store
    //const cartStore = inject("cartStore");

    // Determine initial email value
    const initialEmail = props.userEmail || props.attendee?.user?.email || "";
    const paymentMethods = ref([]);
    const total = ref("");
    const qrCode = ref("");
    const proformaUrl = ref("");
    const variableSymbol = ref("");
    const paymentId = ref(null);
    const bankDetails = ref({});
    const selectedPaymentMethod = ref(null);
    const gatewayData = ref(null);
    const sessionId = cookie(); // Replace with actual session ID method
    const securityQuestion = ref("");
    const securityAnswer = ref("");
    const userEmail = ref(initialEmail);
    const proformaEmail = ref(initialEmail);

    const teleportTarget = computed(() => {
      // In development (localhost), always use '#app'
      if (process.env.NODE_ENV === "development") {
        return "#app";
      }
      // In production, use vueAppConfig's element_id or fallback to '#app'
      return window.vueAppConfig?.settings?.element_id ?? "#app";
    });

    // Set the email value directly to the input field when the modal opens,
    // in case v-model and :value aren't working properly
    const setEmailDirectly = () => {
      setTimeout(() => {
        const emailInput = document.getElementById("invoice-email");
        if (emailInput) {
          emailInput.value = proformaEmail.value;
        } else {
          console.warn("Could not find invoice-email element");
        }
      }, 100);
    };

    const paymentData = ref(null);
    const remainingTime = ref(0);
    const totalDuration = ref(0);
    const isSendingInvoice = ref(false);
    const showProformaModal = ref(false);
    const emailError = ref("");

    watch(
      () => props.attendee?.user?.email,
      (newEmail) => {
        if (newEmail) {
          userEmail.value = newEmail;
          proformaEmail.value = newEmail;
        }
      },
      { immediate: true }
    );
    let countdownTimer = null;

    const formatTime = (seconds) => {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}m ${s}s`;
    };

    onMounted(async () => {
      // Track the payment step of checkout
      trackEvent("cart", "view_step", "payment", null, {
        step_number: 4,
        event_name: "checkout_progress",
      });

      await fetchPaymentMethods();
    });
    watch(paymentId, (newVal) => {
      if (newVal) {
        fetchPaymentDetails();
      }
    });
    onUnmounted(() => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    });

    const fetchPaymentDetails = async () => {
      try {
        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/paycoin/v1/eventPayment`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              brand_id: window.vueAppConfig.info.id,
              session_id: sessionId,
              include_qr: 1,
              event_payment_id: paymentId.value,
            }),
          }
        );
        const data = await response.json();
        if (data.status) {
          paymentData.value = data.data;
          startCountdown();
        } else {
          notify(
            { title: translate("Error"), text: data.message, group: "error" },
            15000
          );
        }
      } catch (error) {
        console.error("Error fetching payment details:", error);
      }
    };

    const startCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
      const expireAt = new Date(paymentData.value.expire_at).getTime();
      const createdAt = new Date(paymentData.value.created_at).getTime();
      totalDuration.value = Math.floor((expireAt - createdAt) / 1000); // in seconds
      countdownTimer = setInterval(() => {
        const now = new Date().getTime();
        remainingTime.value = Math.max(0, Math.floor((expireAt - now) / 1000));
        if (remainingTime.value <= 0) {
          clearInterval(countdownTimer);
        }
      }, 1000);
      // Initialize remainingTime
      remainingTime.value = Math.max(
        0,
        Math.floor((expireAt - new Date().getTime()) / 1000)
      );
    };

    const copyToClipboard = (text) => {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          notify({
            title: translate("Success"),
            text: translate("Address copied to clipboard!"),
            type: "success",
            group: "success",
          });
        })
        .catch(() => {
          notify({
            title: translate("Error"),
            text: translate("Failed to copy address."),
            type: "error",
            group: "error",
          });
        });
    };

    const fetchPaymentMethods = async () => {
      try {
        // Get client_id from Google Analytics cookie if available
        const getGACookie = () => {
          const cookies = document.cookie.split(";");
          let gaClientId = null;

          // First try to get _ga cookie (standard GA cookie)
          for (const cookie of cookies) {
            const [name, value] = cookie.trim().split("=");
            if (name === "_ga") {
              // _ga cookie value format is GA1.2.XXXXXXXXXX.YYYYYYYYYY
              // We want the XXXXXXXXXX.YYYYYYYYYY part as client_id
              const parts = value.split(".");
              if (parts.length >= 3) {
                gaClientId = parts.slice(2).join(".");
                break;
              }
            }
          }

          // If no _ga cookie found, generate a random ID
          if (!gaClientId) {
            gaClientId = "user-" + Math.random().toString(36).substring(2, 15);
          }

          return gaClientId;
        };

        // Get Google Analytics client ID
        const gaClientId = getGACookie();

        // GA client ID is still needed for backend tracking

        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/v2/events/method/list`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              brand_id: window.vueAppConfig.info.id,
              session_id: sessionId,
              // Include GA tracking data with the request
              ga_tracking: {
                client_id: gaClientId,
              },
            }),
          }
        );
        const data = await response.json();
        if (data.status) {
          paymentId.value = data.payment_id;
          paymentMethods.value = data.data.methods;
          total.value = data.data.total.formatted;
          qrCode.value = data.data.qr_code;
          proformaUrl.value = data.data.proforma_url;
          variableSymbol.value = data.data.variable_symbol;
          bankDetails.value = data.data.bank_details;

          // Only set email from API response if not already set from props
          if (!userEmail.value && data.data.attendee?.user?.email) {
            userEmail.value = data.data.attendee.user.email;
            proformaEmail.value = data.data.attendee.user.email;
          }

          // Purchase event tracking is now handled by the backend

          if (data.message) {
            notify(
              {
                title: translate("Info"),
                text: data.message,
                type: "info",
                group: "info",
              },
              15000
            );
          }
        } else {
          notify(
            { title: translate("Error"), text: data.message, group: "error" },
            15000
          );
        }
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };

    const selectPaymentMethod = async (method) => {
      try {
        // Basic UI interaction tracking (payment conversion will be handled by backend)
        trackEvent("cart", "select_payment_method", method.name);

        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/v2/events/method/select`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              brand_id: window.vueAppConfig.info.id,
              session_id: sessionId,
              method_id: method.id,
              redirect_url: window.location.href,
            }),
          }
        );
        const data = await response.json();
        if (data.status) {
          if (data.data.question) {
            securityQuestion.value = data.data.question;
            selectedPaymentMethod.value = method;
          } else {
            gatewayData.value = data.data;
            selectedPaymentMethod.value = method;
            if (data.module_id === 10) {
              loadGoPayScript(data.data.json.gw_url);
            }
          }
          if (data.message) {
            notify({ text: data.message, type: "info", group: "info" }, 15000);
          }
        } else {
          notify(
            { title: translate("Error"), text: data.message, group: "error" },
            15000
          );
        }
      } catch (error) {
        console.error("Error selecting payment method:", error);
      }
    };

    const loadGoPayScript = (gwUrl) => {
      const script = document.createElement("script");
      script.src = "https://gate.gopay.cz/gp-gw/js/embed.js";
      script.async = true;
      script.onload = () => {
        window._gopay.checkout({
          gatewayUrl: gwUrl,
          inline: true,
        });

        // Wait for the iframe to be added to the DOM and then adjust its z-index
        const iframeId = "gopay-payment-iframe";
        const interval = setInterval(() => {
          const iframe = document.getElementById(iframeId);
          if (iframe) {
            iframe.style.zIndex = "2147483647"; // Max possible z-index value
            clearInterval(interval); // Stop checking once the iframe is found and updated
          }
        }, 100); // Check every 100ms
      };
      document.head.appendChild(script);
    };

    const submitSecurityAnswer = async () => {
      try {
        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/v2/events/method/select`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              brand_id: window.vueAppConfig.info.id,
              session_id: sessionId,
              method_id: selectedPaymentMethod.value.id,
              answer: securityAnswer.value,
            }),
          }
        );
        const data = await response.json();
        if (data.status) {
          if (data.paid) {
            notify(
              {
                title: translate("Success"),
                text: translate("Payment has been received successfully"),
                type: "info",
              },
              15000
            );
            securityQuestion.value = "";
            securityAnswer.value = "";
          } else {
            notify(
              { title: translate("Error"), text: data.message, group: "error" },
              15000
            );
          }
        } else {
          notify(
            { title: translate("Error"), text: data.message, group: "error" },
            15000
          );
        }
      } catch (error) {
        console.error("Error submitting security answer:", error);
      }
    };

    // Open modal and initialize with user email
    watch(showProformaModal, (isOpen) => {
      if (isOpen) {
        // Priority order: direct userEmail prop > attendee.user.email > userEmail ref
        if (props.userEmail) {
          proformaEmail.value = props.userEmail;
          // Also update the ref
          userEmail.value = props.userEmail;
        } else if (props.attendee?.user?.email) {
          proformaEmail.value = props.attendee.user.email;
          // Also update the ref
          userEmail.value = props.attendee.user.email;
        } else {
          proformaEmail.value = userEmail.value || "";
        }
        emailError.value = "";

        // Use our dedicated function to set the email directly
        setEmailDirectly();

        // Call it again after a delay to ensure it gets set
        setTimeout(() => {
          setEmailDirectly();
        }, 300);

        // Prevent body scrolling when modal is open
        document.body.style.overflow = "hidden";
      } else {
        // Re-enable scrolling when modal is closed
        document.body.style.overflow = "";
      }
    });

    const sendProformaInvoice = async () => {
      if (isSendingInvoice.value) return;

      // Validate email address
      emailError.value = "";
      if (!proformaEmail.value) {
        emailError.value = translate("Please enter an email address");
        return;
      }

      // Basic email validation using regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(proformaEmail.value)) {
        emailError.value = translate("Please enter a valid email address");
        return;
      }

      isSendingInvoice.value = true;
      try {
        const response = await fetch(
          `${window.vueAppConfig.endpoint}/api/v2/events/method/proforma`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              brand_id: window.vueAppConfig.info.id,
              session_id: sessionId,
              payment_id: paymentId.value,
              email: proformaEmail.value,
            }),
          }
        );

        const data = await response.json();

        if (data.status) {
          // Close the modal
          showProformaModal.value = false;

          notify(
            {
              title: translate("Success"),
              text: translate(
                "Proforma invoice has been sent to the specified email address"
              ),
              type: "info",
              group: "info",
            },
            5000
          );

          // Track the event with the email type (user's own or different)
          const emailType =
            proformaEmail.value === userEmail.value
              ? "own_email"
              : "different_email";
          trackEvent("payment", "proforma_invoice", emailType);
        } else {
          notify(
            {
              title: translate("Error"),
              text:
                data.message || translate("Failed to send proforma invoice"),
              type: "error",
              group: "error",
            },
            15000
          );
        }
      } catch (error) {
        console.error("Error sending proforma invoice:", error);

        notify(
          {
            title: translate("Error"),
            text: translate(
              "Failed to send proforma invoice. Please try again."
            ),
            type: "error",
            group: "error",
          },
          15000
        );
      } finally {
        isSendingInvoice.value = false;
      }
    };

    return {
      paymentMethods,
      total,
      qrCode,
      proformaUrl,
      variableSymbol,
      bankDetails,
      selectedPaymentMethod,
      gatewayData,
      securityQuestion,
      securityAnswer,
      selectPaymentMethod,
      submitSecurityAnswer,
      sendProformaInvoice,
      isSendingInvoice,
      showProformaModal,
      proformaEmail,
      emailError,
      teleportTarget,

      paymentData,
      remainingTime,
      totalDuration,
      formatTime,
      fetchPaymentDetails,
      copyToClipboard,
    };
  },
};
</script>

<style scoped>
/* Main Container */
.payment-method-selection {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  color: #1f2937;
}

/* Common Card Styles */
.payment-card {
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 2rem;
  border: 1px solid #f3f4f6;
  transition: all 0.3s ease;
}

.payment-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.payment-card-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  background-color: #f9fafb;
}

.payment-card-title {
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.payment-card-icon {
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.75rem;
  color: #6366f1;
}

.payment-card-content {
  padding: 1.5rem;
}

/* Success Card */
.success-container {
  margin-bottom: 2.5rem;
}

.success-card {
  background-color: white;
  border-radius: 1.25rem;
  padding: 2.5rem;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  background-image: radial-gradient(
      circle at top right,
      rgba(16, 185, 129, 0.08) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at bottom left,
      rgba(16, 185, 129, 0.08) 0%,
      transparent 50%
    );
  border: 1px solid rgba(16, 185, 129, 0.15);
}

.success-icon-wrapper {
  margin-bottom: 2rem;
}

.success-icon-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100px;
  height: 100px;
  background-color: #10b981;
  border-radius: 50%;
  box-shadow: 0 0 0 15px rgba(16, 185, 129, 0.15);
  margin: 0 auto;
  animation: scale-in 0.5s ease-out forwards;
}

.check-icon {
  color: white;
  font-size: 50px;
  transform: scale(0);
  animation: check-animation 0.5s cubic-bezier(0.3, 1.5, 0.7, 1) 0.25s forwards;
}

.success-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
}

.success-subtitle {
  font-size: 1.25rem;
  color: #4b5563;
  margin-bottom: 1.5rem;
  max-width: 650px;
  margin-left: auto;
  margin-right: auto;
}

.success-text {
  color: #6b7280;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.proforma-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #6366f1;
  color: white;
  font-weight: 600;
  padding: 0.875rem 1.5rem;
  border-radius: 0.75rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}

.proforma-button:hover {
  background-color: #4f46e5;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.25);
}

.button-icon {
  width: 1.25rem;
  height: 1.25rem;
}

/* Bank Transfer Card */
.bank-transfer-card .payment-card-icon {
  color: #3b82f6;
}

.bank-transfer-content {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
}

.qr-code-wrapper {
  padding: 1rem;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  transition: transform 0.3s ease;
}

.qr-code-wrapper:hover {
  transform: scale(1.02);
}

.qr-code-image {
  width: 160px;
  height: 160px;
}

.qr-code-caption {
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: #6b7280;
}

.bank-details-container {
  flex: 1;
  min-width: 280px;
}

.bank-details-grid {
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.bank-detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.bank-detail-label {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
}

.bank-detail-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.bank-detail-value.secondary {
  color: #6b7280;
  font-weight: 500;
}

.bank-detail-value.highlight {
  color: #4f46e5;
}

.invoice-download-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: #eff6ff;
  transition: all 0.2s ease;
}

.invoice-download-link:hover {
  background-color: #dbeafe;
  color: #2563eb;
}

.download-icon {
  width: 1.25rem;
  height: 1.25rem;
}

/* Payment Methods Card */
.payment-methods-card .payment-card-icon {
  color: #f59e0b;
}

.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.payment-method-item {
  background-color: #f9fafb;
  border-radius: 0.75rem;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
}

.payment-method-item:hover {
  background-color: white;
  transform: translateY(-4px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

.payment-method-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.payment-method-logo {
  height: 60px;
  margin-bottom: 1rem;
  transition: transform 0.3s ease;
}

.payment-method-item:hover .payment-method-logo {
  transform: scale(1.05);
}

.payment-method-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem;
}

.payment-method-description {
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
}

/* Security Question Card */
.security-card .payment-card-icon {
  color: #f43f5e;
}

.security-question-content {
  max-width: 600px;
  margin: 0 auto;
}

.security-question-text {
  margin-bottom: 1.5rem;
  font-size: 1.125rem;
  color: #374151;
  line-height: 1.5;
}

.security-answer-input {
  margin-bottom: 1.5rem;
}

.security-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.security-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.security-submit-button {
  width: 100%;
  padding: 0.875rem 1.25rem;
  background-color: #6366f1;
  color: white;
  font-weight: 600;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.security-submit-button:hover {
  background-color: #4f46e5;
}

/* Crypto Card */
.crypto-card .payment-card-icon {
  color: #f7931a;
}

.crypto-countdown {
  margin-bottom: 1.5rem;
}

.countdown-progress {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.countdown-bar-container {
  flex: 1;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.countdown-bar {
  height: 100%;
  background-color: #10b981;
  border-radius: 4px;
  transition: width 1s linear;
}

.countdown-time {
  font-size: 0.9rem;
  font-weight: 600;
  color: #4b5563;
  min-width: 80px;
  text-align: right;
}

.crypto-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.crypto-method-item {
  background-color: #fffbeb;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid #fef3c7;
}

.crypto-method-header {
  padding: 1rem;
  background-color: #f7931a;
  color: white;
}

.crypto-method-name {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.crypto-method-content {
  padding: 1.25rem;
}

.crypto-amount-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.crypto-amount,
.crypto-rate {
  display: flex;
  flex-direction: column;
}

.crypto-amount-label,
.crypto-rate-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.crypto-amount-value,
.crypto-rate-value {
  font-weight: 600;
  color: #111827;
}

.crypto-address-container {
  margin-bottom: 1.25rem;
}

.crypto-address-input-wrapper {
  display: flex;
  gap: 0.5rem;
}

.crypto-address-input {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: #f9fafb;
  font-size: 0.9rem;
  color: #111827;
  font-family: monospace;
}

.crypto-copy-button {
  padding: 0.5rem 0.75rem;
  background-color: #f7931a;
  color: white;
  border: none;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.crypto-copy-button:hover {
  background-color: #e67e22;
}

.crypto-qr-container {
  display: flex;
  justify-content: center;
  margin: 1.25rem 0;
}

.crypto-qr-image {
  max-width: 150px;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.crypto-wallet-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: #f7931a;
  color: white;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.crypto-wallet-link:hover {
  background-color: #e67e22;
}

.wallet-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.crypto-expired {
  text-align: center;
  padding: 2rem 0;
}

.crypto-expired-message {
  font-size: 1.125rem;
  color: #ef4444;
  font-weight: 500;
  margin-bottom: 1.5rem;
}

.crypto-refresh-button {
  padding: 0.75rem 1.25rem;
  background-color: #f7931a;
  color: white;
  border: none;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.crypto-refresh-button:hover {
  background-color: #e67e22;
}

.refresh-icon {
  width: 1.25rem;
  height: 1.25rem;
}

/* Animation Keyframes */
@keyframes scale-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  80% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes check-animation {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* Modal Styles */
.modern-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2147483647;
  animation: fadeIn 0.3s ease;
}

.modern-modal {
  width: 90%;
  max-width: 500px;
  margin: 1.5rem;
  border-radius: 1rem;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideIn 0.3s ease;
}

.modern-modal-content {
  display: flex;
  flex-direction: column;
}

.modern-modal-header {
  background-color: #f9fafb;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
}

.modern-modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modern-close-btn {
  width: 2rem;
  height: 2rem;
  background-color: #f3f4f6;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modern-close-btn:hover {
  background-color: #e5e7eb;
  color: #111827;
}

.modern-modal-body {
  padding: 1.5rem;
}

.modern-modal-description {
  color: #4b5563;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.modern-form-group {
  margin-bottom: 1.5rem;
}

.modern-form-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.modern-input-container {
  position: relative;
}

.modern-input-icon {
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  color: #9ca3af;
  width: 1.25rem;
  height: 1.25rem;
}

.modern-form-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.modern-form-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.modern-form-error {
  margin-top: 0.5rem;
  color: #ef4444;
  font-size: 0.875rem;
}

.modern-modal-footer {
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.modern-cancel-btn {
  padding: 0.75rem 1.25rem;
  background-color: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modern-cancel-btn:hover {
  background-color: #f9fafb;
}

.modern-submit-btn {
  padding: 0.75rem 1.25rem;
  background-color: #6366f1;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modern-submit-btn:hover:not(:disabled) {
  background-color: #4f46e5;
}

.modern-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modern-btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modern-btn-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.modern-loading-icon {
  width: 1.25rem;
  height: 1.25rem;
  animation: spin 1s linear infinite;
}

.modern-loading-circle {
  fill: none;
  stroke: white;
  stroke-width: 2;
  stroke-dasharray: 60;
  stroke-dashoffset: 20;
  stroke-linecap: round;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Styles */
@media (max-width: 768px) {
  .bank-transfer-content {
    flex-direction: column;
    align-items: center;
  }

  .bank-details-container {
    width: 100%;
  }

  .payment-methods-grid,
  .crypto-methods-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .success-title {
    font-size: 1.5rem;
  }

  .success-subtitle {
    font-size: 1rem;
  }

  .proforma-button,
  .security-submit-button,
  .crypto-refresh-button {
    width: 100%;
  }

  .payment-card-title {
    font-size: 1.125rem;
  }

  .bank-detail-value {
    font-size: 1rem;
  }

  .qr-code-image {
    width: 120px;
    height: 120px;
  }
}
</style>
